import { service } from '@ember/service'
import Component from '@glimmer/component'
import { colors } from '@colors'
import { tracked } from '@glimmer/tracking'
import { DataProcessor } from '@blakeelearning/data-tables/utils/data-processor'
import { t, type IntlService } from 'ember-intl'
import type Student from 'teacher-ui-client/models/student'
import type { UIScopeName } from 'teacher-ui-client/reporting/ui-scope/route'
import { differenceWith, find } from 'lodash'
import { htmlSafe, isHTMLSafe } from '@ember/template'
import { Legend, TD, TH, type LegendsItem } from 'teacher-ui-client/components/giraffe/primitives/history-tables'
import { ChartInfoTooltip } from 'teacher-ui-client/components/giraffe/chart-info-tooltip'
import { SortColumnIcon } from 'teacher-ui-client/components/sort-column-icon'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import optional from 'teacher-ui-client/helpers/optional'
import { gradeName } from 'teacher-ui-client/helpers/grade-name'
import { formatValue } from 'teacher-ui-client/helpers/format-value'
import BaseHighcharterComponent from 'teacher-ui-client/components/base/highcharter'
import type RouterService from '@ember/routing/router-service'
import type Owner from '@ember/owner'
import type { AssessmentScoresByGenreHistoryData } from 'teacher-ui-client/utils/student-events/fetchers/assessment-scores-by-genre-history'
import { hasId } from 'teacher-ui-client/utils/type-guards'

interface HistoryDataViewModel {
  firstName: string
  lastName: string
  teacherName: string
  gradePosition: number
  id: string
  studentId: string
  fiction: number | null
  nonfiction: number | null
  lastFictionLexile: number | null
  lastNonfictionLexile: number | null
  quizzesTaken: number | null
}

interface Args {
  assessmentScoresHistoryData: AssessmentScoresByGenreHistoryData[]
  students: Student[]
  uiScope: UIScopeName
  subco: boolean
  tooltip: string
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

function getColor(lexile: number) {
  if (lexile < 50) return colors.watermelonyRed300
  else if (lexile < 80) return colors.juicyOrange300
  else return colors.grapeyGreen300
}

interface ChartArgs {
  value: number
  showYAxisLabels: boolean
  lastLexile: number
}

const buildChartOptions = function (chartOptions: ChartArgs): Highcharts.Options {
  const yAxis: Highcharts.YAxisOptions = {
    title: {
      text: '',
    },
    max: 100, // Set for 100%
    tickInterval: 25, // Show tick every 25%
    allowDecimals: false,
    opposite: true,
    labels: {
      enabled: chartOptions.showYAxisLabels,
      format: '{value}%',
    },
    offset: -10,
  }

  const series = [
    {
      type: 'bar' as const,
      name: 'averageScore',
      data: [chartOptions.value],
    },
  ]

  return {
    series,
    chart: {
      type: 'bar',
      // Make some room for the yaxis labels
      margin: chartOptions.showYAxisLabels ? [20, 0, 0, 0] : [0, 0, 0, 0],
      plotBackgroundColor: colors.dustyBlack50,
      // always disable animation for this chart, as they're all separate charts and don't animate together
      animation: false,
    },
    tooltip: {
      enabled: true,
      formatter: function (this: Highcharts.Point) {
        return `<b>${this.y}%</b>`
      },
      backgroundColor: colors.dustyBlack500,
      borderColor: colors.dustyBlack500,
      style: {
        color: colors.white,
      },
    },
    colors: [getColor(chartOptions.value)],
    credits: {
      enabled: false,
    },
    title: {
      text: '',
    },
    xAxis: {
      maxPadding: 0,
      minPadding: 0,
      labels: {
        enabled: false,
      },
      lineWidth: 0,
      tickWidth: 0,
      gridLineWidth: 0,
      minorGridLineWidth: 0,
    },
    legend: { enabled: false },
    yAxis,
    plotOptions: {
      bar: {
        dataLabels: {
          enabled: chartOptions.value > 0,
          formatter: function (this: Highcharts.Point) {
            return `${chartOptions.lastLexile}L`
          },
          align: 'left',
        },
        animation: false,
        borderWidth: 0,
        colorByPoint: false,
        stacking: 'normal',
        pointWidth: 40,
      },
    },
  }
}

export class AssessmentScoresByGenreHistory extends Component<Signature> {
  @service intl!: IntlService

  @service router!: RouterService

  @tracked sortingPriorityQueue = []

  processor: DataProcessor<HistoryDataViewModel>

  constructor(owner: Owner, args: Args) {
    super(owner, args)
    this.processor = new DataProcessor(this)
  }

  get data(): HistoryDataViewModel[] {
    return this.tableRows
  }

  get tableRows(): HistoryDataViewModel[] {
    const stats = this.args.assessmentScoresHistoryData
    return this.args.students.filter(hasId).map((student) => {
      const statRow = find(stats, { student_id: parseInt(student.id, 10) })

      return {
        firstName: student.firstName,
        lastName: student.lastName,
        teacherName: student.readingTeacherNames.join(', '),
        gradePosition: student.gradePosition,
        id: student.id,
        studentId: student.id,
        fiction: statRow?.fiction ?? null,
        nonfiction: statRow?.nonfiction ?? null,
        lastFictionLexile: statRow?.last_fiction_lexile ?? null,
        lastNonfictionLexile: statRow?.last_nonfiction_lexile ?? null,
        quizzesTaken: statRow?.quizzes_taken ?? null,
      }
    })
  }

  get amendedTooltip() {
    const { tooltip } = this.args
    const missingStudentsCount = differenceWith(
      this.args.students,
      this.args.assessmentScoresHistoryData ?? [],
      (student, historyStudent) => student.id === String(historyStudent.student_id),
    ).length

    if (missingStudentsCount) {
      const wasSafe = isHTMLSafe(tooltip)
      const newTooltip = this.args.tooltip + this.intl.t('reporting.missingStudentsCount', { missingStudentsCount })
      return wasSafe ? htmlSafe(newTooltip) : newTooltip
    }
    return tooltip
  }

  get rowsWithChartOptions() {
    return this.processor.transformedData.map((row, index) => {
      const fictionChartOptions = buildChartOptions({
        value: row.fiction ?? 0,
        showYAxisLabels: index === 0,
        lastLexile: row.lastFictionLexile ?? 0,
      })

      const nonfictionChartOptions = buildChartOptions({
        value: row.nonfiction ?? 0,
        showYAxisLabels: index === 0,
        lastLexile: row.lastNonfictionLexile ?? 0,
      })

      return {
        ...row,
        fictionChartOptions: fictionChartOptions,
        nonfictionChartOptions: nonfictionChartOptions,
      }
    })
  }

  transitionToStudentRoute = (studentId: string) => {
    void this.router.transitionTo('reporting.ui-scope.rex.student-summary', studentId)
  }

  get legendCategories(): LegendsItem[] {
    return [
      {
        label: this.intl.t('reporting.charts.assessmentScores.byGenre.legend.fiction'),
      },
      {
        label: this.intl.t('reporting.charts.assessmentScores.byGenre.legend.nonfiction'),
      },
    ]
  }

  get showChart() {
    return this.args.uiScope !== 'school'
  }

  <template>
    <div
      data-test-giraffe-assessment-scores-by-genre-history
      class="border-dusty-black-50 shadow-dusty-black-100 relative rounded-sm border shadow-md"
      ...attributes
    >
      <ChartInfoTooltip data-test-assessment-scores-by-genre-tooltip @tooltipContent={{this.amendedTooltip}} />
      <div class="flex overflow-x-auto print:block">
        <table
          data-test-history-table-assessment-scores-by-genre
          class="w-full min-w-6xl overflow-hidden rounded-sm text-sm"
        >
          <thead>
            <tr class="bg-juicy-orange-300 divide-x divide-white font-semibold text-white">
              <TH class="w-36" role="button" {{on "click" (fn this.processor.tableActions.sortByValue "firstName")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.dashboardTables.firstName" htmlSafe=true}}
                  <SortColumnIcon class @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="firstName" />
                </div>
              </TH>
              <TH class="w-36" role="button" {{on "click" (fn this.processor.tableActions.sortByValue "lastName")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.dashboardTables.lastName" htmlSafe=true}}

                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="lastName" />
                </div>
              </TH>
              {{#if @subco}}
                <TH
                  class="w-48"
                  role="button"
                  {{on "click" (fn this.processor.tableActions.sortByValue "teacherName")}}
                >
                  <div class="flex items-center justify-between">
                    {{t "reporting.dashboardTables.teacher"}}
                    <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="teacherName" />
                  </div>
                </TH>
                <TH
                  class="w-24"
                  role="button"
                  {{on "click" (fn this.processor.tableActions.sortByNumber "gradePosition")}}
                >
                  <div class="flex items-center justify-between">
                    {{t "grade.label"}}
                    <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="gradePosition" />
                  </div>
                </TH>
              {{/if}}
              <th class="print-only-table-cell w-24 text-center">
                {{t "reporting.charts.assessmentScores.byGenre.recentFictionLexile" htmlSafe=true}}
              </th>
              <th class="print-only-table-cell w-24 text-center">
                {{t "reporting.charts.assessmentScores.byGenre.recentFictionScore" htmlSafe=true}}
              </th>
              <th class="print-only-table-cell w-24 text-center">
                {{t "reporting.charts.assessmentScores.byGenre.recentNonfictionLexile" htmlSafe=true}}
              </th>
              <th class="print-only-table-cell w-24 text-center">
                {{t "reporting.charts.assessmentScores.byGenre.recentNonfictionScore" htmlSafe=true}}
              </th>
              <TH
                class="w-24"
                role="button"
                {{on "click" (fn this.processor.tableActions.sortByNumber "quizzesTaken")}}
              >
                <div class="flex items-center justify-between">
                  {{t "reporting.charts.assessmentScores.byGenre.assessmentsCompleted" htmlSafe=true}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="quizzesTaken" />
                </div>
              </TH>
              {{#if this.showChart}}
                <TH
                  class="cursor-default border-none bg-white px-0! whitespace-nowrap text-black print:hidden"
                  colspan="2"
                >
                  <Legend
                    @categories={{this.legendCategories}}
                    @title={{t "reporting.charts.assessmentScores.byGenre.legend.title" htmlSafe=true}}
                    @multipleCharts={{true}}
                  />
                </TH>
              {{/if}}
            </tr>
          </thead>
          <tbody class="divide-dusty-black-100 divide-y-2 print:divide-black">
            {{#each this.rowsWithChartOptions as |row|}}
              {{! template-lint-disable no-invalid-interactive }}
              <tr class="cursor-pointer" {{on "click" (fn (optional this.transitionToStudentRoute) row.id)}}>
                <TD class="text-oceany-blue-300">
                  {{row.firstName}}
                </TD>
                <TD class="text-oceany-blue-300">
                  {{row.lastName}}
                </TD>
                {{#if @subco}}
                  <TD>
                    <div class="{{if this.showChart 'max-h-20 overflow-auto'}}">
                      {{row.teacherName}}
                    </div>
                  </TD>
                  <TD class="text-center">
                    {{gradeName row.gradePosition}}
                  </TD>
                {{/if}}
                <td class="print-only-table-cell text-center">
                  {{formatValue row.lastFictionLexile append="L"}}
                </td>
                <td class="print-only-table-cell text-center">
                  {{formatValue row.fiction append="%"}}
                </td>
                <td class="print-only-table-cell text-center">
                  {{formatValue row.lastNonfictionLexile append="L"}}
                </td>
                <td class="print-only-table-cell text-center">
                  {{formatValue row.nonfiction append="%"}}
                </td>
                <TD class="text-center">
                  {{formatValue row.quizzesTaken}}
                </TD>
                {{#if this.showChart}}
                  <TD
                    class="border-dusty-black-100
                      {{if @subco 'w-1/5' 'w-1/4'}}
                      cursor-default border-l px-1! py-0! print:hidden"
                  >
                    <BaseHighcharterComponent
                      data-test-assessment-scores-by-genre-highchart-row
                      class="h-full"
                      @options={{row.fictionChartOptions}}
                    />
                  </TD>
                  <TD
                    class="border-dusty-black-100
                      {{if @subco 'w-1/5' 'w-1/4'}}
                      cursor-default border-l px-1! py-0! print:hidden"
                  >
                    <BaseHighcharterComponent
                      data-test-assessment-scores-by-genre-highchart-row
                      class="h-full"
                      @options={{row.nonfictionChartOptions}}
                    />
                  </TD>
                {{/if}}
              </tr>
            {{/each}}
          </tbody>
        </table>
      </div>
    </div>
  </template>
}

export default AssessmentScoresByGenreHistory
